import * as Sentry from '@sentry/react'
import { registerAnalyticsProvider } from './utils/analytics'
import { sentryAnalyticsProvider } from './utils/analyticsProviders'

export const { logger } = Sentry

function getClickSpanName(el: HTMLElement): string {
  const ariaLabel = el.getAttribute('aria-label')
  if (ariaLabel?.trim()) return ariaLabel.trim()
  const labelledById = el.getAttribute('aria-labelledby')
  if (labelledById) {
    const labelEl = document.getElementById(labelledById)
    const label = labelEl?.textContent?.trim()?.slice(0, 50)
    if (label) return label
  }
  const text = el.textContent?.trim()?.slice(0, 50)
  if (text) return text
  const tag = el.tagName.toLowerCase()
  const role = el.getAttribute('role') || ''
  const type = (el as HTMLInputElement).type || ''
  return [tag, role, type].filter(Boolean).join(' ') || 'click'
}

function registerClickInstrumentation(): void {
  if (typeof document === 'undefined') return
  document.addEventListener(
    'click',
    (e) => {
      const target = (e.target as HTMLElement).closest(
        'a, button, [role="button"], [role="menuitem"], input[type="submit"], input[type="button"]'
      ) as HTMLElement | null
      if (!target) return
      const name = getClickSpanName(target)
      Sentry.startSpan(
        {
          op: 'ui.click',
          name: name || 'click',
          attributes: {
            'ui.element': target.tagName.toLowerCase(),
            'ui.role': target.getAttribute('role') || '',
          },
        },
        () => {}
      )
    },
    true
  )
}

export function initializeSentry(): void {
  // Vite only inlines VITE_* present at build time. CI must pass VITE_PUBLIC_SENTRY_DSN (see appBuildConfig.sentryDsn).
  const dsn = import.meta.env['VITE_PUBLIC_SENTRY_DSN'] || import.meta.env['VITE_SENTRY_DSN']
  const environment = import.meta.env['VITE_SENTRY_ENVIRONMENT'] || 'development'

  if (!dsn) {
    console.warn('Sentry DSN not configured. Skipping Sentry initialization.')
    return
  }

  const serviceName = 'variant-lens-ui'

  Sentry.init({
    dsn,
    environment,
    initialScope: {
      tags: { service: serviceName },
    },
    beforeSendTransaction(transaction) {
      ;(transaction as { server_name?: string }).server_name = serviceName
      for (const span of transaction.spans ?? []) {
        const data = (span as { data?: Record<string, unknown> }).data ?? {}
        data['service.name'] = serviceName
        data['server_name'] = serviceName
        ;(span as { data?: Record<string, unknown> }).data = data
        const tags = (span as { tags?: Record<string, string> }).tags ?? {}
        tags['service'] = serviceName
        ;(span as { tags?: Record<string, string> }).tags = tags
      }
      return transaction
    },
    enableLogs: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
      Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
    ],
    tracePropagationTargets: ['localhost', /^https:\/\/.*\.inflectionmedicine\.com$/, /^\/api\//],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
  })

  registerClickInstrumentation()
  registerAnalyticsProvider(sentryAnalyticsProvider)
}
